import { type CSSProperties, type ReactNode, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

export interface DialogProps {
  title: string;
  subtitle?: ReactNode;
  /** Omit to make the dialog non-dismissable (blocking confirmations only). */
  onClose?: () => void;
  /** End-aligned action row; the confirm button goes last. */
  footer?: ReactNode;
  children?: ReactNode;
  /** `default` (detail inspector) or `narrow` (confirmations and short forms). */
  width?: 'default' | 'narrow';
  style?: CSSProperties;
}

/** Modal over a navy scrim. Escape and scrim click both close. */
export function Dialog({ title, subtitle, onClose, footer, children, width = 'default', style }: DialogProps) {
  const { t } = useTranslation();
  const titleId = useId();

  useEffect(() => {
    if (!onClose) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const classes = ['fs-dialog'];
  if (width === 'narrow') classes.push('fs-dialog--narrow');
  return (
    <div className="fs-dialog-scrim" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={classes.join(' ')} style={style}>
        <header className="fs-dialog__header">
          <div className="fs-dialog__heading">
            <h2 id={titleId} className="fs-dialog__title">
              {title}
            </h2>
            {subtitle && <p className="fs-dialog__subtitle">{subtitle}</p>}
          </div>
          {onClose && (
            <button type="button" className="fs-dialog__close" aria-label={t('common.close')} onClick={onClose}>
              <span aria-hidden="true">×</span>
            </button>
          )}
        </header>
        <div className="fs-dialog__body">{children}</div>
        {footer && <footer className="fs-dialog__footer">{footer}</footer>}
      </div>
    </div>
  );
}
